import React, { useState } from "react";
import { CircularLoader, Button, Tooltip } from "@dhis2/ui";
import axios from "axios";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import * as XLSX from "xlsx-js-style";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const customParseFormat = require("dayjs/plugin/customParseFormat");
dayjs.extend(customParseFormat);

const DashboardDownload = ({ email: user, periodFilter: period, type }) => {
  const [downloadStarted, setDownloadStarted] = useState(false);
  const baseURL =
    process.env.NODE_ENV === "development"
      ? "http://localhost:9000/mrunner_api"
      : `${window.location.protocol}//${window.location.host}/mrunner_api`;

  const downloadReport = async () => {
    try {
      setDownloadStarted(true);

      const oPeriod = dayjs(period, "MMM-YYYY").format("M-YYYY");
      const response = await axios.post(`${baseURL}/getMPRData`, {
        user,
        period: [oPeriod],
        type,
      });

      const data = response.data.sort(
        (a, b) => Number(a.sr_no) - Number(b.sr_no)
      );

      if (!data || data.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "No Data",
          text: "No records found.",
        });
        return;
      }

      const mixCols = ["Minimum Package Service"];
      const directCols = ["Additional Services", "Activity Management"];
      const showCount = [
        "Registration",
        "Daily Basic life and other Services",
        "Need Based Linkage for Family Members",
      ];

      const reportHeader = [
        ["Monthly Progress Report"],
        [
          "Name of the State",
          data[0]?.state || "",
          "Name of District",
          data[0]?.district || "",
        ],
        [
          "Name of the Organization",
          "Plan India",
          "Month of Center Establishment",
          "Apr-25",
        ],
        [
          "Reporting month",
          dayjs(oPeriod, "M-YYYY").format("MMM-YY"),
          "Center Name",
          data[0]?.facility || "",
        ],
        [
          "Name of the Site Incharge",
          data[0]?.facility_incharge || "",
          "Contact no. of the Site Incharge",
          data[0]?.contactnumber1 || "",
        ],
        [],
      ];

      const monthsCols = ["Achievement Cumulative", period, "Remark"];
      const totalCols = 2 + monthsCols.length; // for consistent column width

      // --- Group data ---
      const mainMap = new Map();
      data.forEach((item) => {
        const mc = item.maincategory || "Unknown";
        const cat = item.category || "Uncategorized";
        if (!mainMap.has(mc)) mainMap.set(mc, new Map());
        const catMap = mainMap.get(mc);
        if (!catMap.has(cat)) catMap.set(cat, []);
        catMap.get(cat).push(item);
      });

      const sheetData = [...reportHeader];
      const merges = [];
      const styleMeta = {};

      let mainIndex = 1;
      let isFirstMain = true;

      for (const [mainCategory, catMap] of mainMap.entries()) {
        const mainSr = `${mainIndex}`;

        // --- Main category row ---
        const mainRow = Array(totalCols).fill("");
        mainRow[0] = mainSr;
        mainRow[1] = mainCategory;

        // Add monthsCols for first main category row
        if (isFirstMain) {
          for (let i = 0; i < monthsCols.length; i++) {
            mainRow[2 + i] = monthsCols[i];
          }
          isFirstMain = false;
        }

        sheetData.push(mainRow);
        styleMeta[sheetData.length - 1] = { type: "main" };

        // --- Handle Direct Columns ---
        if (directCols.includes(mainCategory)) {
          let descIndex = 1;
          for (const [catName, items] of catMap.entries()) {
            for (const item of items) {
              const descSr = `${mainIndex}.${descIndex++}`;
              const monthCells = Array(monthsCols.length).fill("");
              monthCells[0] = item.cumlative_count ?? 0;
              monthCells[1] = item.count_teid ?? 0;
              sheetData.push([descSr, item.description, ...monthCells]);
              styleMeta[sheetData.length - 1] = { bold: false };
            }
          }
          mainIndex++;
          continue;
        }

        // --- Handle Mix Columns (no sum) ---
        if (mixCols.includes(mainCategory)) {
          let catIndex = 1;
          for (const [catName, items] of catMap.entries()) {
            if (items.length === 1) {
              const item = items[0];
              const monthCells = Array(monthsCols.length).fill("");
              monthCells[0] = item.cumlative_count ?? 0;
              monthCells[1] = item.count_teid ?? 0;
              sheetData.push([
                `${mainIndex}.${catIndex}`,
                item.description,
                ...monthCells,
              ]);
              styleMeta[sheetData.length - 1] = { bold: false };
            } else {
              const catCells = Array(monthsCols.length).fill("");
              sheetData.push([
                `${mainIndex}.${catIndex}`,
                catName,
                ...catCells,
              ]);
              styleMeta[sheetData.length - 1] = {
                bold: true,
                type: "category",
                fillColor: "C6E0B4",
              };

              let descIndex = 1;
              items.forEach((item) => {
                const monthCells = Array(monthsCols.length).fill("");
                monthCells[0] = item.cumlative_count ?? 0;
                monthCells[1] = item.count_teid ?? 0;
                sheetData.push([
                  `${mainIndex}.${catIndex}.${descIndex++}`,
                  item.description,
                  ...monthCells,
                ]);
                styleMeta[sheetData.length - 1] = { bold: false };
              });
            }
            catIndex++;
          }
          mainIndex++;
          continue;
        }

        // --- Default + showCount logic ---
        let catIndex = 1;
        for (const [catName, items] of catMap.entries()) {
          const catSr = `${mainIndex}.${catIndex}`;
          let categorySum = "";
          let cumulativeSum = "";

          if (showCount.includes(mainCategory)) {
            categorySum = items.reduce(
              (sum, item) => sum + (Number(item.count_teid) || 0),
              0
            );
            cumulativeSum = items.reduce(
              (sum, item) => sum + (Number(item.cumlative_count) || 0),
              0
            );
          }

          const catCells = Array(monthsCols.length).fill("");
          catCells[0] = cumulativeSum;
          catCells[1] = categorySum;
          sheetData.push([catSr, catName, ...catCells]);
          styleMeta[sheetData.length - 1] = {
            bold: true,
            type: "category",
            fillColor: "C6E0B4",
          };

          let descIndex = 1;
          items.forEach((item) => {
            const monthCells = Array(monthsCols.length).fill("");
            monthCells[0] = item.cumlative_count ?? 0;
            monthCells[1] = item.count_teid ?? 0;
            sheetData.push([
              `${mainIndex}.${catIndex}.${descIndex++}`,
              item.description,
              ...monthCells,
            ]);
            styleMeta[sheetData.length - 1] = { bold: false };
          });
          catIndex++;
        }

        mainIndex++;
      }

      // --- Build worksheet ---
      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } });
      worksheet["!merges"] = merges;

      // --- Apply styles ---
      const range = XLSX.utils.decode_range(worksheet["!ref"]);

      for (let R = 0; R <= range.e.r; R++) {
        const rowMeta = styleMeta[R] || {};
        const row = sheetData[R] || [];

        for (let C = 0; C < totalCols; C++) {
          const cell = worksheet[XLSX.utils.encode_cell({ r: R, c: C })];
          if (!cell) continue;

          let font = {};
          let fill = undefined;

          // First row - Report Heading
          if (R === 0) {
            font.bold = true;
            font.sz = 14;
            cell.s = {
              font,
              alignment: { horizontal: "center", vertical: "center" },
            };
            continue;
          }

          // Report header keys (like "Name of the State") bold
          if (R > 0 && R < reportHeader.length) {
            if (C % 2 === 0 && row[C]) {
              font.bold = true;
            }
          }

          // Main category row
          if (rowMeta.type === "main") {
            fill = { fgColor: { rgb: "BDD7EE" } }; // Blue Accent 1, Lighter 80%
            font.bold = true;
          } else if (rowMeta.type === "category" && rowMeta.bold) {
            fill = { fgColor: { rgb: "C6E0B4" } }; // Olive Green
            font.bold = true;
          }

          cell.s = {
            font,
            alignment: { horizontal: "left", vertical: "center" },
            ...(fill ? { fill } : {}),
          };
        }
      }

      worksheet["!cols"] = [
        { wch: 10 },
        { wch: 40 },
        ...Array(totalCols - 2).fill({ wch: 18 }),
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "MPR Report");
      XLSX.writeFile(workbook, "MPR_Report.xlsx");
    } catch (err) {
      console.error("Error fetching data:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong while generating the report.",
      });
    } finally {
      setDownloadStarted(false);
    }
  };

  return (
    <div
      className="text-right"
      style={{
        padding: "5px",
        display: "flex",
        justifyContent: "end",
        alignItems: "center",
      }}
    >
      {downloadStarted ? (
        <CircularLoader small className="mt-0 mb-0" />
      ) : (
        <Tooltip content="Download">
          <Button
            small
            type="button"
            value="default"
            className="ml-10px"
            onClick={downloadReport}
          >
            <FileDownloadIcon />
          </Button>
        </Tooltip>
      )}
    </div>
  );
};

export default DashboardDownload;
